import {useQuery} from '@github-ui/react-query'
import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'

type AgenticAutofixJobStatus =
  | 'invalid'
  | 'pending'
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'timed_out'

const terminalStatuses: AgenticAutofixJobStatus[] = ['invalid', 'completed', 'failed', 'cancelled', 'timed_out']

export type AgenticAutofixJob = {
  job_id: string
  session_id?: string
  status?: AgenticAutofixJobStatus
  pull_request?: {id: number; number: number}
  error?: {message: string}
}

function isTerminal(job: AgenticAutofixJob, completeOnPullRequest: boolean) {
  if (job.status && terminalStatuses.includes(job.status)) return true
  return completeOnPullRequest && Boolean(job.pull_request)
}

export interface UseAgenticAutofixJobPollOptions {
  // Set false when the job is tied to an existing pull request from creation (PR-mode autofix).
  completeOnPullRequest?: boolean
}

export function useAgenticAutofixJobPoll(
  owner: string,
  repo: string,
  jobId: string | null,
  onError?: (message: string) => void,
  options?: UseAgenticAutofixJobPollOptions,
) {
  const completeOnPullRequest = options?.completeOnPullRequest ?? true
  const path = jobId ? `/agents/swe/v1/jobs/${owner}/${repo}/${jobId}` : null

  const {data} = useQuery({
    queryKey: ['agentic-autofix-job', path, completeOnPullRequest],
    queryFn: async () => {
      if (!path) throw new Error('Missing polling path')
      const response = await fetchCAPI({
        path,
        method: 'GET',
        useCapi: true,
      })
      const job = (await response.json()) as AgenticAutofixJob
      if (isTerminal(job, completeOnPullRequest) && job.error?.message) {
        onError?.(job.error.message)
      }
      return job
    },
    enabled: !!path,
    refetchInterval: (query: {state: {data?: AgenticAutofixJob}}) => {
      const job = query.state.data
      if (job && isTerminal(job, completeOnPullRequest)) return false
      return 1000
    },
  })

  const isTerminalJob = data ? isTerminal(data, completeOnPullRequest) : false
  const terminalErrorMessage = isTerminalJob ? data?.error?.message : null

  return {
    errorMessage: terminalErrorMessage ?? null,
    isComplete: isTerminalJob && !terminalErrorMessage,
    sessionId: data?.session_id ?? null,
  }
}
